import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from '../DraggableIndexedTable/DraggableIndexedTable.module.scss';
import Switch from '../../common/Switch';
import Spinner from '../../common/Spinner';
import { timeAgoFormatter } from '../../../utils/formatHelper';
import ConfirmModal from '../../../components/modals/ConfirmDeleteModal';
import { arrayMove } from '../../../utils/array';
import ScrollableList from '../../../components/common/ScrollableList/ScrollableList';
import DraggableIndexedTable from '../../../components/blocks/DraggableIndexedTable/DraggableIndexedTable';
import { partition } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { ALERT_ERROR } from '../../../store/constants/alertTypes';
import EmptyData from '../../../components/common/EmptyData';
import history from '../../../utils/history';
import { warehousesIntegrationForms } from '../../../containers/pages/warehouses/WarehouseEditContainer';
import { NO_VALUE_PLACEHOLDER } from '../../../constants/tables';

const mapIndex = (list) => list.map((item, index) => `${item.id}-${index + 1}`);

function WarehousesTable(props) {
  const { data, setIsEnabled, setPriority } = props;
  const [toggling, setToggling] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [warehousesToReorder, setWarehousesToReorder] = useState([]);
  const dispatch = useDispatch();
  const { supplierId } = useSelector((state) => state.role);

  useEffect(() => {
    if (warehousesToReorder.length) {
      const [warehouseId, warehouseNewPriority] = warehousesToReorder[0].split('-');
      setPriority(warehouseId, +warehouseNewPriority);
      setWarehousesToReorder([]);
    }
  }, [warehousesToReorder, setPriority, data]);

  useEffect(() => {
    if (data.length) {
      const warehousesOrderedByStatus = orderWarehousesByStatus(data);
      const orderedWarehousesIndex = mapIndex(warehousesOrderedByStatus);
      const originalWarehousesIndex = mapIndex(data);
      const mismatchingIndexes = orderedWarehousesIndex.filter((element) => !originalWarehousesIndex.includes(element));
      setWarehousesToReorder(mismatchingIndexes);
      setWarehouses(data);
    }
  }, [data]);

  const gotoEditWarehouse = (warehouseId) => {
    history.push(`/suppliers/${supplierId}/warehouses/${warehouseId}/edit`);
  };

  const firstInactiveWarehouseIndex = useMemo(() => {
    if (warehouses.length) {
      const lastActiveWarehouseIndex = warehouses.map((item) => item?.is_enabled).indexOf(false);
      return lastActiveWarehouseIndex === -1 ? warehouses.length + 1 : lastActiveWarehouseIndex + 1;
    }
  }, [warehouses]);

  const orderWarehousesByStatus = (warehouses) => {
    const [activeWarehouses, nonActiveWarehouses] = partition(warehouses, (warehouse) => warehouse.is_enabled);
    return activeWarehouses.concat(nonActiveWarehouses);
  };

  const formatConnectedChannels = (property, row) => {
    const text = property.length ? `${property.length} Channels` : '--';
    return <ScrollableList listId={row.id} list={property} text={text} />;
  };

  const formatIsEnabled = (isEnabled, row) => {
    const warehouseId = row.id;
    const warehouseName = row.name;

    if (toggling.includes(warehouseId)) {
      return <Spinner size="small" />;
    }

    return (
      <Switch
        label={isEnabled ? 'Active' : 'Inactive'}
        checked={isEnabled}
        onSwitch={(value) => toggleIsEnabled(value, warehouseId, warehouseName)}
      />
    );
  };

  const formatEdit = (_, warehouse) => {
    if (!warehousesIntegrationForms.hasOwnProperty(warehouse.orders_integration_type)) {
      return null;
    }

    return (
      <button onClick={() => gotoEditWarehouse(warehouse.id)} className={styles.editButton}>
        Edit
      </button>
    );
  };

  const toggleIsEnabled = (value, warehouseId, warehouseName) => {
    setToggling([...toggling, warehouseId]);

    if (value) {
      setIsEnabled(warehouseId, value, firstInactiveWarehouseIndex);
      setToggling([]);
    } else {
      setConfirmModalOpened({ objectId: warehouseId, warehouseName });
    }
  };

  const columns = [
    {
      dataField: 'priority',
      text: 'Priority',
      classes: styles.priorityColumn,
    },
    {
      dataField: 'drag_icon',
      text: '',
    },
    {
      dataField: 'name',
      text: 'Warehouse',
    },
    {
      dataField: 'inventory_updated_at',
      text: 'Last Inventory Update',
      formatter: (property) => (property ? timeAgoFormatter(property) : NO_VALUE_PLACEHOLDER),
    },
    {
      dataField: 'connected_channels',
      text: 'Connected Channels',
      formatter: formatConnectedChannels,
    },
    {
      dataField: 'is_enabled',
      text: 'Status',
      formatter: formatIsEnabled,
    },
    {
      dataField: 'edit',
      text: '',
      formatter: formatEdit,
      classes: styles.editColumn,
    },
  ];

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const currentMovingRow = warehouses[oldIndex - 1];
    if (oldIndex === newIndex || newIndex >= firstInactiveWarehouseIndex) {
      return;
    }
    if (!currentMovingRow.is_enabled) {
      return dispatch({ type: ALERT_ERROR, payload: `Disabled warehouses can't be reordered` });
    }

    setWarehouses(arrayMove(warehouses, oldIndex, newIndex));

    const warehouseId = currentMovingRow.id;
    setPriority(warehouseId, newIndex);
  };

  function displayContent() {
    return data.length ? (
      <DraggableIndexedTable columns={columns} list={warehouses} onSortEnd={onSortEnd} />
    ) : data.length === 0 ? (
      <EmptyData text="No Warehouses Found" />
    ) : (
      <Spinner size="large" />
    );
  }

  return (
    <>
      <h2>Manage Warehouses</h2>
      <h4 className={styles.info}>Orders will be orchestrated based on priority and inventory availability</h4>
      <br />

      {displayContent()}

      <ConfirmModal
        open={!!confirmModalOpened}
        text={[
          <big>
            <b>Deactivate {confirmModalOpened.warehouseName}?</b>
          </big>,
          <br />,
          <br />,
          `When you deactivate a warehouse:`,
          <br />,
          ` •  We’ll push an inventory update to retailers (to exclude the inventory from this WH)`,
          <br />,
          ` •  New orders will no longer be sent to this WH (in-progress orders will not be affected)`,
          <br />,
          `This action might have an impact on sales.`,
          <br />,
          <br />,
          <br />,
          `Are you sure you want to deactivate?`,
        ]}
        confirmButton={'Deactivate'}
        objectId={confirmModalOpened.objectId}
        onConfirm={(warehouseId) => {
          setIsEnabled(warehouseId, false, firstInactiveWarehouseIndex - 1);
          setConfirmModalOpened(false);
          setToggling([]);
        }}
        onCancel={() => {
          setConfirmModalOpened(false);
          setToggling([]);
        }}
      />
    </>
  );
}

export default WarehousesTable;

WarehousesTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  setIsEnabled: PropTypes.func.isRequired,
  setPriority: PropTypes.func.isRequired,
};
